import type { Bytes } from "@pdfking/tfunc"
import type { PdfEngine } from "../../types/engine/PdfEngine"
import type { RemoteFileChunk } from "../../types/files/cached/RemoteFileChunk"
import type { TaskResult } from "../../types/tasks/input/TaskResult"
import type { TaskDownloadOptions } from "../../types/tasks/options/TaskDownloadOptions"
import type { TrackingCallback } from "../../types/tasks/tracking/TrackingCallback"
import { BlobCreate } from "@pdfking/compatibility"
import {
  ResizeUInt8,
  ResponseContentLength,
  SafePromise,
  StreamToIterable,
} from "@pdfking/tfunc"
import { EngineError } from "../../errors/EngineError"
import { GenerateFile } from "../../files/GenerateFile"
import { ProgressFetch } from "../../network/ProgressFetch"
import { ErrorName } from "../../types/errors/ErrorName"
import { ContentType } from "../../types/files/ContentType"
import { VirtualFileType } from "../../types/files/VirtualFileType"
import { TaskName } from "../../types/tasks/TaskName"
import { TrackedTaskName } from "../../types/tasks/tracking/TrackedTaskName"

export async function* TaskDownload(
  engine: PdfEngine,
  task: TaskDownloadOptions,
  emit: TrackingCallback
): AsyncGenerator<TaskResult> {
  const {
    options: { chunk_size },
    source: remote,
    source: { mirrors },
  } = task

  for (const url of mirrors) {
    const { value: response } = await SafePromise(
      ProgressFetch(url, {
        download: e =>
          emit({
            name: TrackedTaskName.DOWNLOAD,
            ...e,
          }),
      })
    )

    if (response?.ok) {
      const stream = ResizeUInt8(StreamToIterable(response.body), chunk_size)
      const bytes_total = ResponseContentLength(response)

      let bytes_start = 0 as Bytes

      for await (const chunk of stream) {
        const bytes_end = (bytes_start + chunk.length) as Bytes

        yield {
          content: {
            bytes_end,
            bytes_start,
            bytes_total,
            content: await GenerateFile(
              engine,
              [BlobCreate(chunk)],
              ContentType.BYTES,
              emit
            ),
            enum: VirtualFileType.REMOTE_FILE_CHUNK,
            file_meta: remote.file_meta,
            remote,
          } as RemoteFileChunk,
          enum: TaskName.CONTENT_FILE,
          name: `chunk_${bytes_start}.bin`,
        }

        bytes_start = bytes_end
      }

      return
    }
  }

  throw EngineError(ErrorName.FILE_NOT_FOUND)
}
